import React, { useEffect, useMemo, useCallback, useState } from 'react';
import { RangeSliderInput } from 'apps/sigma2-ui/src/filters-v2/components/RangeSliderInput';
import { S2NumberField } from 'apps/sigma2-ui/src/filters-v2/components/S2NumberField';
import { Spacing } from 'apps/sigma2-ui/src/components/ui/Spacing';
import { getTranslatedContentAsString } from 'libs/end-to-end/src/translation-content';
import { TranslatedContent } from 'libs/end-to-end/src/translation-content';

export interface RangeSliderAndTextInputsProps {
  /**
   * The minimum value of the selection
   */
  minimum: number;

  /**
   * The maximum value of the selection
   */
  maximum: number;

  /**
   * The number of decimal places to support step sizes of
   */
  precision: number;

  /**
   * The start value selection
   */
  startValue: number;

  /**
   * The end value selection
   */
  endValue: number;

  /**
   * Callback when the range slider or text inputs change
   * @param newValue
   */
  onChange: (newValue: [number, number]) => void;

  /**
   * The translated content for the start label
   */
  startLabelTranslatedContent: TranslatedContent;

  /**
   * The translated content for the end label
   */
  endLabelTranslatedContent: TranslatedContent;
}

export const RangeSliderAndTextInputs = ({
  precision,
  maximum,
  minimum,
  startValue,
  endValue,
  onChange,
  startLabelTranslatedContent,
  endLabelTranslatedContent,
}: RangeSliderAndTextInputsProps) => {
  const [desiredStartValue, setDesiredStartValue] = useState<number | null>(
    startValue,
  );

  const [desiredEndValue, setDesiredEndValue] = useState<number | null>(
    endValue,
  );

  // Any time the parent changes the startValue, update the desiredStartValue
  useEffect(() => {
    setDesiredStartValue(startValue);
  }, [startValue]);

  // Any time the parent changes the endValue, update the desiredEndValue
  useEffect(() => {
    setDesiredEndValue(endValue);
  }, [endValue]);

  const sliderChangeCb = useCallback(
    (newValue: [number, number]) => onChange(newValue),
    [onChange],
  );

  const inputDisabled = useMemo(() => minimum === maximum, [minimum, maximum]);

  return (
    <Spacing direction="vertical" size="lg">
      <RangeSliderInput
        onChange={sliderChangeCb}
        minimum={minimum}
        maximum={maximum}
        values={[startValue, endValue]}
        precision={precision}
      />

      <Spacing direction="vertical" size="">
        <S2NumberField
          label={getTranslatedContentAsString(startLabelTranslatedContent)}
          value={desiredStartValue}
          min={minimum}
          max={endValue}
          disabled={inputDisabled}
          onValueChange={setDesiredStartValue}
          onValueCommitted={(value) => {
            if (value !== null) {
              onChange([value, endValue]);
            }
          }}
          format={{ useGrouping: false, maximumFractionDigits: precision }}
        />

        <S2NumberField
          label={getTranslatedContentAsString(endLabelTranslatedContent)}
          value={desiredEndValue}
          min={startValue}
          max={maximum}
          disabled={inputDisabled}
          onValueChange={setDesiredEndValue}
          onValueCommitted={(value) => {
            if (value !== null) {
              onChange([startValue, value]);
            }
          }}
          format={{ useGrouping: false, maximumFractionDigits: precision }}
        />
      </Spacing>
    </Spacing>
  );
};
